<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('devices.flowMetersHubs.name') }}
      </v-toolbar-title>
    </template>

    <template #content>
      <v-skeleton-loader
        v-if="loading"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <VTableRemote
        :key="componentKey"
        :iconHeader=" 'add'"
        :items="displayedFlowMetersHub"
        :headers="tableHeaders"
        :sortableColumns="sortableColumns"
        :itemActions="itemActions"
        :disablePagination="true"
        :pagination="{}"
        hideDefaultFooter
        :isCustomIcon="true"
        :isInfinitScroll="true"
        :isSearchable="false"
        :title="$t('devices.flowMetersHubs.name')"
        :centerColumns="['action']"
        :notSortableColumns="['action']"
        startIconName="cell_tower"
        showExpand
        @iconAction="onClickAction"
        @sendQueryParams="fetchQuery"
      >
        <template #SearchWithFilters>
          <SearchWithFilters
            :filters="[]"
            :searchPlaceholder="$t('controls.search')"
            :filterName="filterName"
            @onSearch="handleSearch"
          />
        </template>
        <template #expanded-item="props">
          <td :colspan="props.headers.length">
            <FlowMeterDetails
              :meterHub="props.item"
              @reloadController="reload"
            />
          </td>
        </template>
        <template #item.connectionType="{ item }">
          <p> {{ $t(`devices.flowMetersHubs.connectionType.${item.connectionType}`) }}</p>
        </template>
      </VTableRemote>
    </template>
  </BoxWithTitle>
</template>

<script>
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import Vue from 'vue';
import { mapGetters } from 'vuex';

import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import { INDIRECT, DIRECT } from '@/aquassist-shared/constants/types/channels';
import FlowMeterHubForm from './flowMeterHub/FlowMeterHubForm.vue';
import FlowMeterDetails from './flowMeter/FlowMeterDetails.vue';


export default {
    name: 'FlowMetersHubTab',
    components: {
        VTableRemote,
        BoxWithTitle,
        SearchWithFilters,
        FlowMeterDetails,
    },
    data() {
        return {
            loading: false,
            componentKey: 0,
            flowMeterHubsList: [],
            filterName: 'flowMetersHub',
            queryParams: { },
        };
    },
    computed: {
        ...mapGetters([
            'site',
            'hasPermission',
            'hasRole',
            'teamID',
        ]),
        tableHeaders() {
            const headers = [
                {
                    text: this.$t('devices.name'),
                    value: 'name',
                    sortable: false,
                },
                {
                    text: this.$t('devices.make'),
                    value: 'make',
                    sortable: false,
                },
                {
                    text: this.$t('devices.model'),
                    value: 'model',
                    sortable: false,
                },
                {
                    text: this.$t('devices.macAddress'),
                    value: 'macAddress',
                    sortable: false,
                },
                {
                    text: this.$t('devices.connectionType'),
                    value: 'translatedConnectionType',
                    sortable: false,
                },
            ];
            headers.unshift({
                text: '',
                value: 'icon',
                sortable: false,
            });

            return headers;
        },
        sortableColumns() {
            return this.tableHeaders
                .filter(column => column.sortable)
                .map(column => this.$options.filters.lowercase(column.value));
        },
        displayedFlowMetersHub() {
            // Helper function to create a details object with channel information for empty hub channel
            const createDetailsObject = (details, channelNumber, type) => ({
                ...details,
                channel: `Channel ${channelNumber} - ${type}`,
                channelNumber,
                resetAt: 65000,
            });

            // Process each document to build the configuration of the hub
            return this.flowMeterHubsList.documents.map(hub => {
                const channelsWithDetails = [];
                // Process controller channels
                hub.indirectChannels.forEach((id, index) => {
                    const channelNumber = index + 1;
                    const channelType = INDIRECT;
                    if (id) { // If id is not null, find the details and add channel info
                        const channelDetails = hub.indirectChannelsDetails.find(detail => detail.id === id);
                        channelsWithDetails.push(createDetailsObject(channelDetails, channelNumber, channelType));
                    } else { // If id is null, create an empty object with channel info
                        const emptyDetails = createDetailsObject({}, channelNumber, channelType);
                        channelsWithDetails.push(emptyDetails);
                    }
                });

                // Process direct channels
                const offset = hub.indirectChannels.length;
                hub.directChannels.forEach((id, index) => {
                    const channelNumber = index + 1 + offset;
                    const channelType = DIRECT;
                    if (id) { // If id is not null, find the details and add channel info
                        const channelDetails = hub.directChannelsDetails.find(detail => detail.id === id);
                        channelsWithDetails.push(createDetailsObject(channelDetails, channelNumber, channelType));
                    } else { // If id is null, create an empty object with channel info
                        const emptyDetails = createDetailsObject({}, channelNumber, channelType);
                        channelsWithDetails.push(emptyDetails);
                    }
                });

                // Process another channel type ...
                const translatedConnectionType = this.translateConnectionType(hub.connectionType);
                const channelSortedByNumber = channelsWithDetails.sort((a, b) => a.channelNumber - b.channelNumber);

                return {
                    ...hub,
                    translatedConnectionType,
                    channelSortedByNumber,
                };
            });
        },
        itemActions() {
            const actions = [];
            if (this.hasPermission('controllers.edit')) {
                actions.push({ name: this.$t('devices.flowMetersHubs.update'), icon: 'edit' });
            }
            if (this.hasPermission('controllers.remove')) {
                actions.push({ name: this.$t('devices.flowMetersHubs.remove'), icon: 'delete', iconAndTextColor: '#EF4135' });
            }
            return actions;
        },
    },
    async created() {
        await this.getFlowMetersHubAndChanelConfig();
    },

    methods: {
        translateConnectionType(connectionType) {
            const translationKey = `devices.flowMetersHubs.connectionType.${connectionType}`;
            return this.$t(translationKey) || connectionType;
        },
        async fetchQuery(queryParams) {
            this.queryParams = {
                search: queryParams.search,
            };
            await this.getFlowMetersHubs(this.site.id);
        },

        async onClickAction(item, action) {
            const itemId = item.id;
            if (!action && item === 'add') {
                await this.addFlowMeterHub();
            }
            if (action) {
                switch (action?.icon) {
                    case 'delete':
                        await this.onClickDeleteFlowMeterHub(itemId);
                        break;
                    case 'edit':
                        await this.editFlowMeterHub(item);
                        break;
                    default:
                        break;
                }
            }
        },
        async reload() {
            this.flowMetersHub = [];
            await this.getFlowMetersHubAndChanelConfig();
        },
        handleSearch(search) {
            this.fetchQuery({ search });
        },
        async getFlowMetersHubAndChanelConfig() {
            this.loading = true;

            const { data } = await this.$rq.getFlowMetersHubs(this.site.id);
            this.flowMeterHubsList = data;
            this.loading = false;
        },
        /* Ajouter un Hub (flowmeter) depuis le gros bouton rouge "Ajouter" */
        async addFlowMeterHub() {
            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMetersHubs.add'),
                // A Remettre
                component: Vue.component('flowMeterForm', FlowMeterHubForm),
                payload: { },
                onConfirm: requestPayload => this.onConfirmAddMeterHub(requestPayload),
                width: 800,
                isForm: true,
            });
        },
        async onConfirmAddMeterHub(requestPayload) {
            const payload = {
                ...requestPayload,
                site: this.site.id,
                team: this.teamID,
            };

            await this.$rq.addFlowMeterHub(this.site.id, payload);
            await this.reload();
        },
        async onClickDeleteFlowMeterHub(itemId) {
            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMetersHubs.remove'),
                component: Vue.component('confirmRemove', ConfirmForm),
                payload: { msg: this.$t('devices.flowMetersHubs.removeMsg') },
                onConfirm: async () => {
                    await this.$rq.deleteFlowMetersHub(this.site.id, itemId);
                    await this.reload();
                },
                isForm: false,
            });
        },
        async editFlowMeterHub(flowMeterHub) {
            this.$root.$emit('showModal', {
                title: this.$t('devices.flowMetersHubs.update'),
                component: Vue.component('FlowMeterHubForm', FlowMeterHubForm),
                payload: { flowMeterHub },
                onConfirm: async requestPayload => {
                    if (!requestPayload) {
                        return;
                    }
                    const payload = {
                        ...requestPayload,
                        site: this.site.id,
                        team: this.teamID,
                    };

                    const propertiesToRemove = [
                        'createdAt',
                        'updatedAt',
                        'indirectChannelsDetails',
                        'directChannelsDetails',
                        'id',
                        'channelSortedByNumber',
                        'translatedConnectionType',
                    ];

                    // Remove the listed properties
                    propertiesToRemove.forEach(prop => {
                        delete payload[prop];
                    });

                    await this.$rq.editFlowMeterHub(this.site.id, flowMeterHub.id, payload);
                    await this.reload();
                },
                width: 800,
                isForm: true,
            });
        },
    },

};

</script>
